import React, { useState } from 'react';
import { Calculator } from '../../types';
import { CalculatorShell } from './CalculatorShell';
import { exportToPDF, shareToWhatsApp } from '../../utils/exportUtils';
import { AllCalculatorsModule } from './AllCalculatorsModule';

// Import individual calculators
import BudgetCalculator from './planning/BudgetCalculator';
import FinancialGoalCalculator from './planning/FinancialGoalCalculator';
import TravelPlanningCalculator from './planning/TravelPlanningCalculator';

interface PlanningModuleProps {
  calculator: Calculator;
  onBack: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

export const PlanningModule: React.FC<PlanningModuleProps> = ({
  calculator,
  onBack,
  isFavorite,
  onToggleFavorite,
}) => {
  // Rolar para o topo ao entrar na calculadora
  React.useEffect(() => {
    window.scrollTo(0, 0);
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [calculator.slug]);

  const [exportData, setExportData] = useState<{ title: string, results: { label: string, value: string }[] } | null>(null);

  const handleExportPDF = () => {
    if (!exportData) return;
    exportToPDF({
      title: exportData.title,
      description: `Relatório gerado em ${new Date().toLocaleDateString('pt-BR')}`,
      results: exportData.results
    });
  };

  const handleShareWhatsApp = () => {
    if (!exportData) return;
    shareToWhatsApp(exportData.title, exportData.results);
  };

  const handleResultChange = React.useCallback((data: { label: string, value: string }[]) => {
    setExportData({ title: calculator.title, results: data });
  }, [calculator.title]);

  const supportedSlugs = [
    'orcamento-mensal',
    'meta-financeira',
    'viagem'
  ];

  if (!supportedSlugs.includes(calculator.slug)) {
    return <AllCalculatorsModule calculator={calculator} onBack={onBack} />;
  }

  const renderPlanningCalculator = () => {
    switch (calculator.slug) {
      case 'orcamento-mensal':
        return <BudgetCalculator onResultChange={handleResultChange} />;
      case 'meta-financeira':
        return <FinancialGoalCalculator onResultChange={handleResultChange} />;
      case 'viagem':
        return <TravelPlanningCalculator onResultChange={handleResultChange} />;
      default:
        return null;
    }
  };

  return (
    <CalculatorShell
      calculator={calculator}
      onBack={onBack}
      isFavorite={isFavorite}
      onToggleFavorite={onToggleFavorite}
      onExportPDF={exportData ? handleExportPDF : undefined}
      onShareWhatsApp={exportData ? handleShareWhatsApp : undefined}
    >
      {renderPlanningCalculator()}
    </CalculatorShell>
  );
};
